/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DashboardSwmComponent.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CustomDataGridV2 from "../../../common/datagrid/CustomDataGridV2";
import { componentStyle } from "../../../../../config/appTheme";
import { useNavigate } from "react-router-dom";

function DashboardSwmComponent({
  recentActivitiesDetails,
  getUserFullNameById,
}) {
  const formControlRef = useRef(null);
  const navigate = useNavigate();
  const [swMTableRowsData, setSWMTableRowsData] = useState([]);
  const [enableMore, setEnableMore] = useState(false);

  useEffect(() => {
    const swmDetails = recentActivitiesDetails?.swmDetails || [];
    setEnableMore(swmDetails.length > 5);
    const filteredswmDetails = [...swmDetails]
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 5)
      .map((item) => ({
        id: item?.swm_id,
        number: item?.name,
        description: item?.description,
        version: item?.version,
        vehicle_functions: item?.vehicle_functions,
        created_by: item?.owner_details?.[0]?.name,
        allData: item,
      }));

    setSWMTableRowsData(filteredswmDetails);
  }, [recentActivitiesDetails]);

  const SWM_COLUMNS = [
    {
      field: "number",
      headerName: "Number",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "vehicle_functions",
      headerName: "Vehicle Functions",
      flex: 1,
      renderCell: (params) => {
        const vehicleFunctions = params?.row?.vehicle_functions || [];
        const defaultValue =
          vehicleFunctions.length > 0 ? vehicleFunctions[0].name : "";

        return (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FormControl
              ref={formControlRef}
              fullWidth
              sx={{ ...componentStyle.form.select.formControl }}
            >
              {vehicleFunctions.length > 0 ? (
                <Select
                  value={defaultValue}
                  displayEmpty
                  sx={{
                    ...componentStyle.form.select.select,
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: formControlRef.current?.offsetWidth || "225px",
                        borderRadius: 0,
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                  renderValue={(selected) => selected || "Vehicle Function"}
                >
                  {vehicleFunctions?.map((func, index) => (
                    <MenuItem
                      sx={{
                        ...componentStyle.form.select.menuItem,
                        "&:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },

                        "&.Mui-selected": {
                          backgroundColor: "#ffffff !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                      key={func.id || func.name || index}
                      value={func.name}
                    >
                      {func.name} ({func.abbreviation})
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography sx={{ ...componentStyle.font.tableText }}>
                  No Vehicle Functions Available
                </Typography>
              )}
            </FormControl>
          </Box>
        );
      },
    },

    {
      field: "created_by",
      headerName: "Owner",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ ...componentStyle.font.tableText }}>
          {getUserFullNameById(params?.value || "-")}
        </Box>
      ),
    },
  ];
  return (
    <Box sx={{ height: "450px", width: "100%" }}>
      <CustomDataGridV2
        columns={SWM_COLUMNS}
        rows={swMTableRowsData}
        enableMore={enableMore}
        handleMore={() => {
          navigate("/dashboard/v2/EESWM");
        }}
      />
    </Box>
  );
}

DashboardSwmComponent.propTypes = {
  recentActivitiesDetails: PropTypes.shape({
    swmDetails: PropTypes.array,
  }),
  getUserFullNameById: PropTypes.func,
};

export default DashboardSwmComponent;
